import React from "react";
import { ErrorMessage, Field } from "formik";
import { profileImagesType } from "ps-shared";
import { ImageCropper, ImgCropCb } from "src/components/General/Cropper";
import { FormGroup, Label } from "reactstrap";

interface ProfileImagesProps {
  values: profileImagesType;
  slug?: string;
}
interface ProfileImagesState {
  maxFileSizeKb: number;
  width: number;
  height: number;
  croppers: any[];
}

interface ProfileImage {
  img: string;
  thumb: string;
}
interface HashTable<T> {
  [key: number]: T;
}

export default class ProfileImagesForm extends React.Component<ProfileImagesProps, ProfileImagesState> {
  cropperMapper: HashTable<ProfileImage> = {};

  constructor(props: ProfileImagesProps) {
    super(props);
    this.state = {
      croppers: [],
      maxFileSizeKb: 1024,
      width: 800,
      height: 800,
    };
    this.onComplete = this.onComplete.bind(this);
    this.cancel = this.cancel.bind(this);
  }

  public componentDidMount() {
    const croppers: any[] = [];
    if (this.props.values.images) {
      // if there are images, then add each cropper image
      // tslint:disable-next-line:prefer-for-of
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.props.values.images.length; i++) {
        // create a cropper (preview)
        croppers.push({ img: this.props.values.images[i], thumb: this.props.values.thumbs[i] });
      }
    }
    // add at least one cropper
    croppers.push({});
    this.setState({
      croppers,
    });
  }

  public onComplete(event: ImgCropCb, form: any) {
    this.persistImg(event.img, event.thumb, form);
    const { croppers } = this.state;
    croppers.push({});
    this.setState({
      croppers,
    });
  }

  public cancel(d: any, form: any) {
    // filter out the cropper
    const remainingCroppers = this.state.croppers.filter((c) => {
      return c.img !== d.img && c.thumb !== d.thumb;
    });
    // if remaining croppers have a src already, then add a new blank cropper
    if (remainingCroppers.every((c) => c.img && c.thumb)) {
      remainingCroppers.push({});
    }
    // filter the image from the source list
    if (d && d.img && d.thumb) {
      const imgs = this.props.values.images.filter((v) => {
        return v !== d.img;
      });
      const thumbs = this.props.values.thumbs.filter((v) => {
        return v !== d.thumb;
      });
      form.setFieldValue("images", imgs);
      form.setFieldValue("thumbs", thumbs);
    }
    // sort the remaining croppers by those with images (ie not the blank one pending upload)
    this.setState({
      croppers: remainingCroppers,
    });
  }

  // tslint:disable:max-line-length
  public render() {
    return (
      <div className="manage-images">
        <FormGroup>
          <Label for="images">Images:</Label>
          <br />
          <ErrorMessage name="images" component="div" className="form-feedback" />
          <ul className="help-text">
            <li>Upload an Hi-Res image that is at least 800 x 800 pixels. Max 30MB</li>
            <li>Click + / - to zoom in or out. Drag image to move.</li>
            <li>Click check mark when satisfy. Click X to load a new image.</li>
            <li>
              For privacy, all EXIF data is stripped from your photo; Learn more about{" "}
              <a href="https://en.wikipedia.org/wiki/Exif#Privacy_and_security" target="_blank" rel="noreferrer">
                EXIF data
              </a>
            </li>
            <li>
              If you can’t get your your high-quality image to be at 800 x 800 pixels, please email the image to us at{" "}
              <a href="mailto:info@psychedelic.support">info@psychedelic.support</a> and we will size it for you.
            </li>
          </ul>
        </FormGroup>
        <div className="mt-3 d-flex flex-row flex-wrap justify-content-center justify-content-sm-start">
          {this.state.croppers.map((d: any, i: number) => {
            return (
              <div key={i} className="cropper">
                <Field>
                  {({ form }: { field: any; form: any }) => (
                    <ImageCropper
                      src={d.img}
                      width={800}
                      height={800}
                      maxFileSizeKb={30720}
                      name={this.props.slug}
                      complete={(event: ImgCropCb) => this.onComplete(event, form)}
                      cancel={() => this.cancel(d, form)}
                    />
                  )}
                </Field>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  private persistImg(img: string, thumb: string, form: any) {
    const images = [...this.props.values.images];
    const thumbs = [...this.props.values.thumbs];
    images.push(img);
    thumbs.push(thumb);
    form.setFieldValue("images", images);
    form.setFieldValue("thumbs", thumbs);
  }
}
